import React, { useEffect, useMemo, useState } from 'react';
import { Box, Spinner, Text } from '@chakra-ui/react';
import axios from 'axios';
import dayjs from 'dayjs';
import { baseUrl } from '../../../../hooks/useEndpoints';
import { ISession } from '../../../../models/sessions';
import { IPeriod } from '../../hooks/useCarichiTable';
import {
  leoTokens,
  roleColors,
  getInitials,
  primaryRole,
  installLeoStyles,
} from '../../theme/leoTokens';

/* ──────────────────────────────────────────────────────────────────────────
 * CalendarSubview — Carichi Esterni
 * Vista atleti × giorni con session chip colorati per tipo.
 * Drop-in replacement del file originale: props invariati, aggiunto field
 * opzionale `roles` su rosterAthletes per colorare il badge ruolo.
 * ────────────────────────────────────────────────────────────────────────── */

interface CalendarSubviewProps {
  teamId: string;
  rosterAthletes: {
    _id: string;
    firstName: string;
    lastName: string;
    roles?: string[];
  }[];
  period: IPeriod;
  onCellClick?: (date: string, athleteId?: string) => void;
}

type SessionTypeKey =
  | 'training'
  | 'match'
  | 'friendly'
  | 'test'
  | 'individual'
  | 'rehab';

const SESSION_TYPE: Record<
  SessionTypeKey,
  { label: string; bg: string; fg: string; dot: string }
> = {
  training: { label: 'Allen.', bg: leoTokens.successSoft, fg: leoTokens.successInk, dot: leoTokens.success },
  match: { label: 'PARTITA', bg: '#fff4d6', fg: '#7a5d10', dot: leoTokens.brandStrong },
  friendly: { label: 'Amich.', bg: '#edf5fa', fg: '#06384d', dot: '#5b8ca6' },
  test: { label: 'Test', bg: '#f3eafd', fg: '#4a2270', dot: '#9970d9' },
  individual: { label: 'Indiv.', bg: leoTokens.warningSoft, fg: leoTokens.warningInk, dot: leoTokens.warning },
  rehab: { label: 'Rehab', bg: leoTokens.errorSoft, fg: leoTokens.errorInk, dot: leoTokens.error },
};

const DAY_SHORT_IT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

export const CalendarSubview: React.FC<CalendarSubviewProps> = ({
  teamId, rosterAthletes, period, onCellClick,
}) => {
  useEffect(() => { installLeoStyles(); }, []);
  const [sessions, setSessions] = useState<ISession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Array giorni — cap 31 per evitare gridoni infiniti
  const days = useMemo(() => {
    const list: string[] = [];
    let cur = dayjs(period.start);
    const end = dayjs(period.end);
    while (cur.isBefore(end) || cur.isSame(end)) {
      list.push(cur.format('YYYY-MM-DD'));
      cur = cur.add(1, 'day');
    }
    return list.slice(0, 31);
  }, [period]);

  // Fetch sessioni del team in singola chiamata.
  // Sostituisce N fetch parallele per-atleta (una chiamata per ogni atleta del
  // roster) che con team grandi saturavano il rate limit 10 req/min/IP → 429.
  // Backend: GET /sessions/team/:teamId/list?from&to ritorna le sessioni
  // dove athleteIds ∈ team.users nel range richiesto.
  useEffect(() => {
    if (!teamId || rosterAthletes.length === 0) { setSessions([]); return; }
    setLoading(true); setError(null);
    const from = dayjs(period.start).format('YYYY-MM-DD');
    const to = dayjs(period.end).format('YYYY-MM-DD');
    axios
      .get<ISession[]>(
        `${baseUrl}/sessions/team/${teamId}/list?from=${from}&to=${to}`,
      )
      .then((res) => setSessions(res.data ?? []))
      .catch((e: any) => setError(e?.message ?? 'Errore'))
      .finally(() => setLoading(false));
  }, [teamId, rosterAthletes, period]);

  // Index: athleteId → YYYY-MM-DD → sessions[]
  const grid = useMemo(() => {
    const map = new Map<string, Map<string, ISession[]>>();
    for (const s of sessions) {
      const dKey = dayjs(s.date).format('YYYY-MM-DD');
      for (const aId of s.athleteIds) {
        if (!map.has(aId)) map.set(aId, new Map());
        const dayMap = map.get(aId)!;
        if (!dayMap.has(dKey)) dayMap.set(dKey, []);
        dayMap.get(dKey)!.push(s);
      }
    }
    return map;
  }, [sessions]);

  if (loading) return <Box className="leo-scope" p={8} textAlign="center"><Spinner /><Text mt={2} color={leoTokens.muted}>Carico calendario…</Text></Box>;
  if (error) return <Box className="leo-scope" p={8} textAlign="center" color={leoTokens.errorInk}>{error}</Box>;

  const rangeLabel = dayjs(period.start).format('DD MMM') + ' — ' + dayjs(period.end).format('DD MMM YYYY');

  return (
    <Box className="leo-scope">
      <Box px={4} py={3} bg={leoTokens.bg2} borderBottom="1px solid" borderColor={leoTokens.divider}
        display="flex" alignItems="center" gap={3} flexWrap="wrap">
        <Box as="span" px={2.5} py={1} borderRadius="6px" bg={leoTokens.brand} color={leoTokens.brandInk} fontSize="12px" fontWeight={700}>Periodo</Box>
        <Text fontSize="13px" fontWeight={500} color={leoTokens.ink}>{rangeLabel}</Text>
        <Box w="1px" h="18px" bg={leoTokens.dividerStrong} />
        <Text fontSize="12px" color={leoTokens.muted}>{rosterAthletes.length} atleti · {days.length} giorni · click cella per dettaglio</Text>
        <Box ml="auto" display="flex" gap={3} alignItems="center" flexWrap="wrap">
          {(Object.keys(SESSION_TYPE) as SessionTypeKey[]).map((k) => (
            <Box key={k} display="flex" alignItems="center" gap="5px" fontSize="11px" color={leoTokens.muted}>
              <Box w="8px" h="8px" borderRadius="2px" bg={SESSION_TYPE[k].dot} />
              {SESSION_TYPE[k].label}
            </Box>
          ))}
        </Box>
      </Box>

      <Box overflowX="auto" bg="var(--c-card-bg)">
        <table>
          <thead>
            <tr>
              <th style={{ position: 'sticky', top: 0, left: 0, zIndex: 5, background: leoTokens.headerBg, padding: '10px 12px 10px 28px',
                borderBottom: `1px solid ${leoTokens.dividerStrong}`, borderRight: `1px solid ${leoTokens.divider}`,
                textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: leoTokens.muted, minWidth: 240 }}>
                Atleta
              </th>
              {days.map((d) => {
                const dObj = dayjs(d); const isSunday = dObj.day() === 0;
                return (
                  <th key={d} style={{ position: 'sticky', top: 0, zIndex: 4, background: isSunday ? leoTokens.brandSoft : leoTokens.headerBg,
                    padding: '10px 8px', borderBottom: `1px solid ${leoTokens.dividerStrong}`, borderLeft: `1px solid ${leoTokens.divider}`,
                    textAlign: 'center', minWidth: 110 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: leoTokens.ink }}>{DAY_SHORT_IT[dObj.day()]} {dObj.format('DD')}</div>
                    <div className="leo-mono" style={{ fontSize: 10, color: leoTokens.muted, marginTop: 2 }}>{dObj.format('MM/DD')}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rosterAthletes.map((a, idx) => {
              const role = primaryRole(a.roles);
              const stripe = idx % 2 === 1 ? leoTokens.rowStripe : '#ffffff';
              const rc = roleColors(role);
              return (
                <tr key={a._id}>
                  <td style={{ padding: '6px 12px 6px 28px', position: 'sticky', left: 0, zIndex: 1, background: stripe,
                    borderRight: `1px solid ${leoTokens.divider}`, borderBottom: `1px solid ${leoTokens.rowDivider}`, minWidth: 240 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 26, height: 26, borderRadius: '50%', background: rc.bg, color: rc.fg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 700 }}>
                        {getInitials(a.firstName, a.lastName)}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: leoTokens.ink }}>{a.firstName} {a.lastName}</div>
                      {role && (
                        <span style={{ marginLeft: 'auto', fontSize: 9.5, fontWeight: 700, color: rc.fg,
                          padding: '0 5px', borderRadius: 3, background: rc.bg, letterSpacing: 0.4 }}>{role}</span>
                      )}
                    </div>
                  </td>
                  {days.map((d) => {
                    const aSessions = grid.get(a._id)?.get(d) ?? [];
                    const isSunday = dayjs(d).day() === 0;
                    return (
                      <td key={d} onClick={() => onCellClick?.(d, a._id)}
                        style={{ padding: 4, borderBottom: `1px solid ${leoTokens.rowDivider}`, borderLeft: `1px solid ${leoTokens.rowDivider}`,
                          background: isSunday ? '#fff9e8' : stripe, verticalAlign: 'top', cursor: onCellClick ? 'pointer' : 'default' }}>
                        {aSessions.length === 0 ? (
                          <div style={{ height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', color: leoTokens.mutedSoft, fontSize: 14 }}>·</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {aSessions.map((s) => {
                              const key = (s.sessionType || 'training') as SessionTypeKey;
                              const t = SESSION_TYPE[key] ?? SESSION_TYPE.training;
                              return (
                                <div key={String(s._id)} title={`${s.name ?? s.sessionType} · ${s.duration ?? '?'}min · TD ${s.commonMetrics?.td ?? '—'}`}
                                  style={{ padding: '3px 6px', background: t.bg, color: t.fg, borderRadius: 4, fontSize: 10.5, fontWeight: 600,
                                    display: 'flex', alignItems: 'center', gap: 5, borderLeft: `2px solid ${t.dot}` }}>
                                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.label}</span>
                                  {s.duration != null && (
                                    <span className="leo-mono" style={{ fontWeight: 500, opacity: 0.85 }}>{s.duration}'</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};
